import { useState, useCallback, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { ZoomIn, ZoomOut, RotateCcw, X } from 'lucide-react'
import { useModalFocus } from '../hooks/useModalFocus'

const MAX_SCALE = 5
const SCALE_STEP = 0.15

const ImageLightbox = ({ src, alt, isOpen, onClose }) => {
  const [scale, setScale] = useState(1)
  const [fitScale, setFitScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const imgRef = useRef(null)
  const dragStart = useRef({ x: 0, y: 0 })
  const translateStart = useRef({ x: 0, y: 0 })
  const pinchDistRef = useRef(null)
  const pinchScaleRef = useRef(1)

  const { modalRef } = useModalFocus(isOpen, {
    onClose,
    preventBodyScroll: true
  })

  // Reset transform when opening or changing image
  useEffect(() => {
    if (isOpen) {
      setFitScale(1)
      setScale(1)
      setTranslate({ x: 0, y: 0 })
    }
  }, [isOpen, src])

  const handleImageLoad = useCallback(() => {
    const img = imgRef.current
    if (!img) return
    const { naturalWidth, naturalHeight } = img
    if (!naturalWidth || !naturalHeight) return
    const viewW = window.innerWidth * 0.85
    const viewH = window.innerHeight * 0.85
    const fit = Math.min(viewW / naturalWidth, viewH / naturalHeight)
    const newFit = fit > 1 ? fit : 1
    setFitScale(newFit)
    setScale(newFit)
  }, [])

  const clampScale = useCallback(
    (s) => {
      const min = fitScale * 0.5
      const max = Math.max(MAX_SCALE, fitScale * 3)
      return Math.min(max, Math.max(min, s))
    },
    [fitScale]
  )

  const handleZoomIn = useCallback(() => {
    const step = SCALE_STEP * 2 * Math.max(1, fitScale)
    setScale((s) => clampScale(s + step))
  }, [clampScale, fitScale])

  const handleZoomOut = useCallback(() => {
    const step = SCALE_STEP * 2 * Math.max(1, fitScale)
    setScale((s) => clampScale(s - step))
  }, [clampScale, fitScale])

  const handleReset = useCallback(() => {
    setScale(fitScale)
    setTranslate({ x: 0, y: 0 })
  }, [fitScale])

  // Scroll wheel zoom
  const handleWheel = useCallback(
    (e) => {
      e.preventDefault()
      const step = SCALE_STEP * Math.max(1, fitScale)
      const delta = e.deltaY > 0 ? -step : step
      setScale((s) => clampScale(s + delta))
    },
    [clampScale, fitScale]
  )

  // Click-to-zoom at base scale
  const handleImageClick = useCallback(() => {
    if (scale <= fitScale * 1.01) {
      setScale(clampScale(fitScale * 2))
    }
  }, [scale, fitScale, clampScale])

  // Pointer drag for panning when zoomed
  const handlePointerDown = useCallback(
    (e) => {
      if (scale <= fitScale * 1.01 || e.pointerType === 'touch') return
      e.preventDefault()
      setIsDragging(true)
      dragStart.current = { x: e.clientX, y: e.clientY }
      translateStart.current = { ...translate }
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [scale, fitScale, translate]
  )

  const handlePointerMove = useCallback(
    (e) => {
      if (!isDragging) return
      const dx = e.clientX - dragStart.current.x
      const dy = e.clientY - dragStart.current.y
      setTranslate({
        x: translateStart.current.x + dx,
        y: translateStart.current.y + dy
      })
    },
    [isDragging]
  )

  const handlePointerUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Touch events for pinch-to-zoom and pan
  const handleTouchStart = useCallback(
    (e) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX
        const dy = e.touches[0].clientY - e.touches[1].clientY
        pinchDistRef.current = Math.hypot(dx, dy)
        pinchScaleRef.current = scale
      } else if (e.touches.length === 1 && scale > fitScale * 1.01) {
        setIsDragging(true)
        dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
        translateStart.current = { ...translate }
      }
    },
    [scale, fitScale, translate]
  )

  const handleTouchMove = useCallback(
    (e) => {
      if (e.touches.length === 2 && pinchDistRef.current !== null) {
        e.preventDefault()
        const dx = e.touches[0].clientX - e.touches[1].clientX
        const dy = e.touches[0].clientY - e.touches[1].clientY
        const dist = Math.hypot(dx, dy)
        const ratio = dist / pinchDistRef.current
        setScale(clampScale(pinchScaleRef.current * ratio))
      } else if (e.touches.length === 1 && isDragging) {
        const dx = e.touches[0].clientX - dragStart.current.x
        const dy = e.touches[0].clientY - dragStart.current.y
        setTranslate({
          x: translateStart.current.x + dx,
          y: translateStart.current.y + dy
        })
      }
    },
    [isDragging, clampScale]
  )

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) {
      pinchDistRef.current = null
    }
    if (e.touches.length === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose]
  )

  if (!isOpen) return null

  const content = (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-label={alt ? `Enlarged view of ${alt}` : 'Enlarged image view'}
      tabIndex={-1}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85"
      onClick={handleBackdropClick}
    >
      {/* Controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <button
          onClick={handleZoomOut}
          aria-label="Zoom out"
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
        >
          <ZoomOut className="w-5 h-5 text-white" aria-hidden="true" />
        </button>
        <button
          onClick={handleReset}
          aria-label="Reset zoom"
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
        >
          <RotateCcw className="w-5 h-5 text-white" aria-hidden="true" />
        </button>
        <button
          onClick={handleZoomIn}
          aria-label="Zoom in"
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
        >
          <ZoomIn className="w-5 h-5 text-white" aria-hidden="true" />
        </button>
        <button
          onClick={onClose}
          aria-label="Close lightbox"
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
        >
          <X className="w-6 h-6 text-white" aria-hidden="true" />
        </button>
      </div>

      {/* Image container */}
      <div
        className="select-none"
        style={{
          touchAction: 'none',
          cursor: scale > fitScale * 1.01 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
        }}
        onClick={handleImageClick}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          ref={imgRef}
          onLoad={handleImageLoad}
          src={src}
          alt={alt || 'Enlarged image'}
          draggable={false}
          className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl bg-white"
          style={{
            transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
            transition: isDragging ? 'none' : 'transform 0.15s ease-out'
          }}
        />
      </div>

      {/* Scale indicator — shows percentage relative to fitScale */}
      {Math.abs(scale - fitScale) > 0.01 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
          {Math.round((scale / fitScale) * 100)}%
        </div>
      )}
    </div>
  )

  return typeof document !== 'undefined' ? ReactDOM.createPortal(content, document.body) : null
}

export default ImageLightbox
